"use client";

import * as React from "react";
import { useEffect } from "react";

import classNames from "classnames";
import { gsap } from "gsap";

import { LinkButton } from "@stadium/shared/src/components/shared/button";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { usePlasmicQueryData } from "@stadium/services/plasmic";

import { fetchPlasmicCMSData } from "@stadium/shared/src/helpers/plasmic/cms-api";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import {
  PLASMIC_BLOGS_QUERY_KEY,
  BE01_TABLE_ID,
  CMS_FIELDS,
  CMS_SECTION_FIELDS,
  CARD_TYPES,
  SLIDER_WIDTH,
  SCROLL_ANIMATION_DURATION,
} from "./constants";

import styles from "./main.module.scss";

interface UICard {
  id: string;
  image: string;
  imageAlt: string;
  categoryText: string;
  categoryUrl: string;
  description?: string;
  type?: "tall" | "regular";
}

export interface BE01Props {
  className?: string;
}

const BE01: React.FC<Readonly<BE01Props>> = ({ className }) => {
  const { isMobileDevice } = React.useContext(CurrentDeviceContext);
  const isDesktop = !isMobileDevice;

  const cardsWrapperRef = React.useRef<HTMLDivElement>(null);
  const cardsContainerRef = React.useRef<HTMLDivElement>(null);

  const [sliderPosition, setSliderPosition] = React.useState(0);
  const [sliderWidth, setSliderWidth] = React.useState(25);
  const sliderTrackRef = React.useRef<HTMLDivElement>(null);

  const scrollState = React.useRef({ targetX: 0 });

  // Fetch data using usePlasmicQueryData
  const { data: cmsData, isLoading } = usePlasmicQueryData([PLASMIC_BLOGS_QUERY_KEY], async () => {
    const data = await fetchPlasmicCMSData({ tableId: BE01_TABLE_ID });

    if (!data) return null;

    const mappedCards: UICard[] = (data[CMS_SECTION_FIELDS.BLOG_CARD] || []).map((item: any) => {
      const cardType = item[CMS_FIELDS.CARD_TYPE];
      let type: "tall" | "regular" = CARD_TYPES.REGULAR;

      if (cardType === CARD_TYPES.TALL) {
        type = CARD_TYPES.TALL;
      }

      return {
        image: item[CMS_FIELDS.IMAGE]?.url,
        imageAlt: item[CMS_FIELDS.IMAGE_ALT] || "",
        categoryText: item[CMS_FIELDS.BUTTON_TEXT],
        categoryUrl: item[CMS_FIELDS.BUTTON_URL],
        description: item[CMS_FIELDS.DESCRIPTION],
        type,
      };
    });

    return {
      heading: data[CMS_SECTION_FIELDS.HEADING] || "",
      seeAllText: data[CMS_SECTION_FIELDS.SEE_ALL_TEXT] || "",
      seeAllUrl: data[CMS_SECTION_FIELDS.SEE_ALL_URL] || "",
      cards: mappedCards,
    };
  });

  const sectionTitle = cmsData?.heading || "";
  const seeAllText = cmsData?.seeAllText || "";
  const seeAllUrl = cmsData?.seeAllUrl || "";
  const cards = cmsData?.cards || [];

  const updateSlider = React.useCallback((percentage: number, visibleRatio: number) => {
    const widthPercent = Math.max(
      SLIDER_WIDTH.MIN_PERCENT,
      Math.min(SLIDER_WIDTH.MAX_PERCENT, visibleRatio * 100),
    );
    setSliderWidth(widthPercent);

    const maxPosition = 100 - widthPercent;
    const position = percentage * maxPosition;
    setSliderPosition(position);
  }, []);

  // Desktop: GSAP wheel-based scrolling
  useEffect(() => {
    if (isDesktop !== true || cards.length === 0) return;

    const container = cardsContainerRef.current;
    const wrapper = cardsWrapperRef.current;

    if (!container || !wrapper) return;

    const state = scrollState.current;
    state.targetX = 0;
    let activeTween: gsap.core.Tween | null = null;

    const getMaxScroll = () => Math.max(0, container.scrollWidth - wrapper.clientWidth);
    const getVisibleRatio = () => wrapper.clientWidth / container.scrollWidth;

    updateSlider(0, getVisibleRatio());

    const animateToTarget = () => {
      const maxScroll = getMaxScroll();
      const visibleRatio = getVisibleRatio();

      if (activeTween !== null) {
        activeTween.kill();
      }

      activeTween = gsap.to(container, {
        x: -state.targetX,
        duration: SCROLL_ANIMATION_DURATION,
        ease: "power2.out",
        onUpdate: () => {
          const currentX = gsap.getProperty(container, "x") as number;
          const percentage = maxScroll > 0 ? Math.abs(currentX) / maxScroll : 0;
          updateSlider(percentage, visibleRatio);
        },
      });
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const maxScroll = getMaxScroll();
      const scrollAmount = event.deltaY * 1.5;

      state.targetX = Math.max(0, Math.min(maxScroll, state.targetX + scrollAmount));

      animateToTarget();
    };

    let touchLastX = 0;

    const handleTouchStart = (event: TouchEvent) => {
      touchLastX = event.touches[0].clientX;
      if (activeTween !== null) {
        activeTween.kill();
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      event.preventDefault();

      const maxScroll = getMaxScroll();
      const delta = touchLastX - event.touches[0].clientX;
      touchLastX = event.touches[0].clientX;

      state.targetX = Math.max(0, Math.min(maxScroll, state.targetX + delta));

      animateToTarget();
    };

    const handleResize = () => {
      const maxScroll = getMaxScroll();
      const visibleRatio = getVisibleRatio();
      state.targetX = Math.min(state.targetX, maxScroll);
      gsap.set(container, { x: -state.targetX });
      const percentage = maxScroll > 0 ? state.targetX / maxScroll : 0;
      updateSlider(percentage, visibleRatio);
    };

    wrapper.addEventListener("wheel", handleWheel, { passive: false });
    wrapper.addEventListener("touchstart", handleTouchStart, { passive: true });
    wrapper.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("resize", handleResize);

    return () => {
      wrapper.removeEventListener("wheel", handleWheel);
      wrapper.removeEventListener("touchstart", handleTouchStart);
      wrapper.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("resize", handleResize);
      if (activeTween !== null) {
        activeTween.kill();
      }
      gsap.set(container, { x: 0 });
    };
  }, [cards, isDesktop, updateSlider]);

  // Mobile/tablet: native scroll on the outer wrapper
  useEffect(() => {
    if (isDesktop !== false || cards.length === 0) return;

    const wrapper = cardsWrapperRef.current;
    if (!wrapper) return;

    const handleScroll = () => {
      const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
      const visibleRatio = wrapper.clientWidth / wrapper.scrollWidth;
      const scrollPercentage = maxScroll > 0 ? wrapper.scrollLeft / maxScroll : 0;
      updateSlider(scrollPercentage, visibleRatio);
    };

    handleScroll();
    wrapper.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      wrapper.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [cards, isDesktop, updateSlider]);

  const renderCard = (card: UICard, index: number) => (
    <div
      key={index}
      className={classNames({
        [styles.tallCard]: card.type === CARD_TYPES.TALL,
        [styles.blogCard]: card.type !== CARD_TYPES.TALL,
      })}
    >
      <Multimedia type="image" src={card.image} alt={card.imageAlt} className={styles.cardImage} />

      <div
        className={classNames({
          [styles.cardOverlay]: card.description == null,
          [styles.featuredOverlay]: card.description != null,
        })}
      >
        <LinkButton styleType="no-style" href={card.categoryUrl} className={styles.linkGroup}>
          <span className={styles.linkPill}>{card.categoryText}</span>
          <span className={styles.linkCircle}></span>
        </LinkButton>

        {card.description && (
          <div className={styles.description}>
            <p className={styles.descriptionContent}>{card.description}</p>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return null;
  }

  return (
    <section className={`${styles.sectionWrapper} ${className || ""}`}>
      <div className={styles.divWrapper}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>{sectionTitle}</h2>
          <LinkButton styleType="no-style" href={seeAllUrl} className={styles.seeAllLink}>
            <span className={styles.seeAllText}>{seeAllText}</span>
            <span className={styles.arrowUpIcon}></span>
          </LinkButton>
        </div>

        <div
          className={isDesktop ? styles.cardsWrapper : styles.mobileCarouselWrapper}
          ref={cardsWrapperRef}
        >
          <div
            className={isDesktop ? styles.cardsContainer : styles.mobileCardWrapper}
            ref={cardsContainerRef}
          >
            {cards.map((card, index) => renderCard(card, index))}
          </div>
        </div>

        {/* Slider - All Devices */}
        <div className={styles.paginationSection}>
          <div ref={sliderTrackRef} className={styles.sliderTrack}>
            <div
              className={styles.sliderHandle}
              style={{ left: `${sliderPosition}%`, width: `${sliderWidth}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BE01;
